import Ember from 'ember';
import { action } from '@ember/object';
import Service from '@ember/service';
import AnalyticsService from './analytics';
import { inject as service } from '@ember/service';
import AccountService from './account';
import RouterService from '@ember/routing/router-service';

export enum PerformanceMetric {
  JourneysLoadingGearsDisplay = 'journeysLoadingGearsDisplay',
  JourneysLoadingGearsHide = 'journeysLoadingGearsHide',
  LoadingStateEnd = 'loadingStateEnd',
}

export type PerformanceMetrics = Record<PerformanceMetric, number | null>;

interface RecordMetricOptions {
  // If not provided, will use window.performance.now()
  // Can be used for sending values gathered from outside of the service
  // (e.g. before Journeys is loaded)
  value?: number;
}

export default class PerformanceService extends Service {
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service router!: RouterService;

  metrics: PerformanceMetrics = {
    journeysLoadingGearsDisplay: null,
    journeysLoadingGearsHide: null,
    loadingStateEnd: null,
  };

  private hasTriedToOverrideMetric = false;
  private hasSentMetrics = false;

  @action
  recordMetric(name: PerformanceMetric, { value }: RecordMetricOptions = {}) {
    if (this.metrics[name] != null) {
      this.hasTriedToOverrideMetric = true;
      return;
    }

    let recordValue = Math.round(value ?? window.performance?.now() ?? 0);

    this.metrics[name] = recordValue;

    let haveAllMetricsBeenRecorded = Object.values(this.metrics).every((metric) => metric != null);

    if (haveAllMetricsBeenRecorded) {
      /** Sanity check to avoid sending metrics that are too high.
       * This can happen, for example, if the user navigates to
       * Journeys after spending some time on Pipelines, so the
       * performance.now() value becomes skewed.
       * Might treat this special case in the future, but for now
       * just excluding these cases is good enough. */
      if ((this.metrics.journeysLoadingGearsDisplay ?? 0) > 60_000) {
        return;
      }

      this.sendMetrics();
    }
  }

  @action
  sendMetrics() {
    // Since these metrics depend on timing, they can be
    // non-deterministic and cause flaky tests.
    if (Ember.testing === true) {
      return;
    }

    if (this.hasSentMetrics) {
      return;
    }

    let data = {
      accountId: this.account.current?.id,
      dataCenter: this.account.current?.dataCenter,
      routeName: this.router.currentRouteName,

      // TODO: Temporary sanity check, should be removed once
      // we gather more data and make sure that all metrics are
      // being recorded only once (for example, should catch
      // cases where the LoadingGears are displayed multiple times)
      hasTriedToOverrideMetric: this.hasTriedToOverrideMetric,
      ...this.metrics,
    };

    this.analytics.track('performance', 'initial_load', data);

    this.hasSentMetrics = true;
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    performance: PerformanceService;
  }
}
